import {useEffect, useRef, useState} from "react";
import {CampusLockUp} from "./Forms/campus-lock-up";
import {EquipmentRequest} from "./Forms/equipment-request";
import {PlatformChangeRequestForm} from "./Forms/platform-change-request";
import {LiveServicesTechRequestForm} from "./Forms/LiveServiceTechRequest";
import {PerformingArtsTechRequestForm} from "./Forms/PerformArtsTechRequest";
import axios from "axios";
import {useNavigate, useParams} from "react-router-dom";
import {SERVER_URL, ParseAPI} from "../API";

export const MasterCalendarForm = () => {

    const { id } = useParams();
    const [isViewOnly, setIsViewOnly] = useState(false);
    const [formData, setFormData] = useState({
        eventName: '',
        department: '',
        dayDateOfEvent: '',
        startTime: '',
        endTime: '',
        prepTime: '',
        locationRequested: '',
        estimatedAttendance: '',
        eventOverseer: '',
        criticalThinkers: false,
        competentCommunicators: false,
        communityContributors: false,
        lifeLongLearners: false,
        christCentered: false,
        sloExplanation: '',
        additionalDetails: '',
        campusLockUp: false,
        equipmentRequest: false,
        platformChangeRequest: false,
        liveServicesTechRequest: false,
        securityPersonnel: false,
        sprinklerAdjustment: false,
        trafficConeAdjustment: false,
        campusLockUpForm: {},
        equipmentRequestForm: {},
        platformChangeRequestForm: {},
        liveServicesTechRequestForm: {},
        performingArtsTechRequestForm: {},
        // Recurring event fields
        isRecurring: false,
        recurrenceType: 'weekly', // daily, weekly, monthly
        recurrenceEndDate: '',
        recurrenceDays: [], // For weekly: 0=Sunday, 1=Monday, etc.
        recurrenceInterval: 1 // Every X days/weeks/months
    });

    const [loading, setLoading] = useState(false);
    const [formSaved, setFormSaved] = useState(false);
    const user = JSON.parse(localStorage.getItem('user'));
    const platformRef = useRef(null);
    const navigate = useNavigate();

    // Fetch form data if ID is provided using ParseAPI
    useEffect(() => {
        // Check if we have duplicated form data in localStorage
        const duplicatedFormData = localStorage.getItem('duplicatedFormData');
        
        // If we're creating a new form and have duplicated data, use it
        if (!id && duplicatedFormData) {
            try {
                const parsedData = JSON.parse(duplicatedFormData);
                
                // Process the duplicated data
                const processedData = {
                    ...parsedData,
                    // Ensure date and time fields are cleared
                    dayDateOfEvent: '',
                    startTime: '',
                    endTime: '',
                    prepTime: '',
                    // Set sub-forms
                    campusLockUpForm: parsedData.campusLockUpForm || {},
                    equipmentRequestForm: parsedData.equipmentRequestForm || {},
                    platformChangeRequestForm: parsedData.platformChangeRequestForm || {},
                    liveServicesTechRequestForm: parsedData.liveServicesTechRequestForm || {},
                    performingArtsTechRequestForm: parsedData.performingArtsTechRequestForm || {}
                };
                
                // Handle case of JSON string form data
                if (parsedData.campusLockUpFormData) {
                    processedData.campusLockUpForm = JSON.parse(parsedData.campusLockUpFormData);
                }
                if (parsedData.equipmentRequestFormData) {
                    processedData.equipmentRequestForm = JSON.parse(parsedData.equipmentRequestFormData);
                }
                if (parsedData.platformChangeRequestFormData) {
                    processedData.platformChangeRequestForm = JSON.parse(parsedData.platformChangeRequestFormData);
                }
                if (parsedData.liveServicesTechRequestFormData) {
                    processedData.liveServicesTechRequestForm = JSON.parse(parsedData.liveServicesTechRequestFormData);
                }
                if (parsedData.performingArtsTechRequestFormData) {
                    processedData.performingArtsTechRequestForm = JSON.parse(parsedData.performingArtsTechRequestFormData);
                }
                
                // Update form data with duplicated data
                setFormData(prevFormData => ({
                    ...prevFormData,
                    ...processedData
                }));
                
                // Clear the duplicated data from localStorage to prevent reuse
                localStorage.removeItem('duplicatedFormData');
            } catch (error) {
                console.error('Error parsing duplicated form data:', error);
                localStorage.removeItem('duplicatedFormData');
            }
            return;
        }
        
        // Regular form loading logic for existing forms
        if (!id) return;
        
        const fetchData = async () => {
            setLoading(true);
            try {
                // Use ParseAPI helper to fetch the form
                const formObject = await ParseAPI.getFormById(id);
                
                if (formObject) {
                    // Convert Parse Object to plain object
                    const parsedData = formObject.toJSON();
                    
                    // Process sub-forms
                    const processedData = {
                        ...parsedData,
                        // Convert Parse pointers to actual form data
                        campusLockUpForm: parsedData.campusLockUpForm || {},
                        equipmentRequestForm: parsedData.equipmentRequestForm || {},
                        platformChangeRequestForm: parsedData.platformChangeRequestForm || {},
                        liveServicesTechRequestForm: parsedData.liveServicesTechRequestForm || {},
                        performingArtsTechRequestForm: parsedData.performingArtsTechRequestForm || {}
                    };
                    
                    // Handle case of draft forms where form data is stored as JSON string
                    if (parsedData.isDraft) {
                        if (parsedData.campusLockUpFormData) {
                            processedData.campusLockUpForm = JSON.parse(parsedData.campusLockUpFormData);
                        }
                        if (parsedData.equipmentRequestFormData) {
                            processedData.equipmentRequestForm = JSON.parse(parsedData.equipmentRequestFormData);
                        }
                        if (parsedData.platformChangeRequestFormData) {
                            processedData.platformChangeRequestForm = JSON.parse(parsedData.platformChangeRequestFormData);
                        }
                        if (parsedData.liveServicesTechRequestFormData) {
                            processedData.liveServicesTechRequestForm = JSON.parse(parsedData.liveServicesTechRequestFormData);
                        }
                        if (parsedData.performingArtsTechRequestFormData) {
                            processedData.performingArtsTechRequestForm = JSON.parse(parsedData.performingArtsTechRequestFormData);
                        }
                    }
                    
                    // Set view-only mode if form is not a draft
                    setIsViewOnly(!parsedData.isDraft);
                    
                    setFormData(prevFormData => ({
                        ...prevFormData,
                        ...processedData
                    }));
                } else {
                    // Fallback to old API if Parse returns no results
                    throw new Error('Form not found in Parse');
                }
            } catch (error) {
                console.error('Error fetching form data:', error);
                // Fallback to old API if Parse query fails
                try {
                    const response = await fetch(`${SERVER_URL}/forms/id/${id}`);
                    const data = await response.json();
                    
                    // Set view-only mode based on form status
                    setIsViewOnly(!data.isDraft);
                    
                    setFormData(prevFormData => ({
                        ...prevFormData,
                        ...data
                    }));
                } catch (fallbackError) {
                    console.error('Fallback fetch also failed:', fallbackError);
                }
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id]);

    // Calculate the number of recurring events
    const calculateRecurrenceCount = () => {
        if (!formData.isRecurring || !formData.dayDateOfEvent || !formData.recurrenceEndDate) {
            return 0;
        }

        const startDate = new Date(formData.dayDateOfEvent);
        const endDate = new Date(formData.recurrenceEndDate);

        if (endDate <= startDate) {
            return 0;
        }

        const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));

        if (formData.recurrenceType === 'daily') {
            return Math.floor(daysDiff / formData.recurrenceInterval) + 1;
        } else if (formData.recurrenceType === 'weekly') {
            const weeksDiff = Math.floor(daysDiff / 7);
            const daysSelected = formData.recurrenceDays.length || 1;
            return Math.floor((weeksDiff / formData.recurrenceInterval) * daysSelected) + 1;
        } else if (formData.recurrenceType === 'monthly') {
            const monthsDiff = (endDate.getFullYear() - startDate.getFullYear()) * 12 +
                              (endDate.getMonth() - startDate.getMonth());
            return Math.floor(monthsDiff / formData.recurrenceInterval) + 1;
        }

        return 0;
    };

    // Generate recurring event dates
    const generateRecurringDates = () => {
        if (!formData.isRecurring || !formData.dayDateOfEvent || !formData.recurrenceEndDate) {
            return [formData.dayDateOfEvent];
        }

        const dates = [];
        const startDate = new Date(formData.dayDateOfEvent);
        const endDate = new Date(formData.recurrenceEndDate);
        let currentDate = new Date(startDate);

        if (formData.recurrenceType === 'daily') {
            while (currentDate <= endDate) {
                dates.push(currentDate.toISOString().split('T')[0]);
                currentDate.setDate(currentDate.getDate() + formData.recurrenceInterval);
            }
        } else if (formData.recurrenceType === 'weekly') {
            // For weekly recurrence, use selected days or the start day if none selected
            const selectedDays = formData.recurrenceDays.length > 0
                ? formData.recurrenceDays
                : [startDate.getDay()];

            while (currentDate <= endDate) {
                if (selectedDays.includes(currentDate.getDay())) {
                    dates.push(currentDate.toISOString().split('T')[0]);
                }
                currentDate.setDate(currentDate.getDate() + 1);

                // Skip ahead by weeks if we've completed a week cycle
                if (currentDate.getDay() === 0 && dates.length > 0) {
                    currentDate.setDate(currentDate.getDate() + (formData.recurrenceInterval - 1) * 7);
                }
            }
        } else if (formData.recurrenceType === 'monthly') {
            while (currentDate <= endDate) {
                dates.push(currentDate.toISOString().split('T')[0]);
                currentDate.setMonth(currentDate.getMonth() + formData.recurrenceInterval);
            }
        }

        return dates;
    };

    // Handle form field changes
    const handleChange = (event) => {
        const { name, type, value, checked } = event.target;
        const newValue = type === 'checkbox' ? checked : value;
        setFormData({
            ...formData,
            [name]: newValue
        });
    };

    // Handle updates from child components
    const handleUpdate = (childName, childData) => {
        setFormData(prevFormData => ({
            ...prevFormData,
            [childName]: childData
        }));
    };

    // Handle form submission using ParseAPI
    const handleSubmit = async () => {
        try {
            setLoading(true);

            // Get platform canvas data if available
            let platformData = null
            if (platformRef.current != null) {
                platformData = await platformRef.current.exportImage()
            }

            // Determine additional signatures needed
            var additionalSignatures = []
            if (formData.campusLockUp === true) { additionalSignatures.push(5) }
            if (formData.securityPersonnel === true || formData.trafficConeAdjustment === true) { additionalSignatures.push(6) }
            if (formData.sprinklerAdjustment === true) { additionalSignatures.push(7) }
            if (formData.platformChangeRequest === true || formData.liveServicesTechRequest === true) { additionalSignatures.push(8) }

            // Check if this is a recurring event
            if (formData.isRecurring) {
                // Generate all recurring dates
                const recurringDates = generateRecurringDates();

                // Create a form submission for each date
                const savePromises = recurringDates.map(async (eventDate) => {
                    const formDataToSave = {
                        ...formData,
                        dayDateOfEvent: eventDate,
                        platformImage: platformData,
                        additionalSignatures: additionalSignatures,
                        // Mark each as part of a recurring series
                        recurringSeriesId: `${user.email}_${Date.now()}`,
                        isRecurringInstance: true
                    };

                    // Save each form instance
                    return ParseAPI.saveForm(formDataToSave, user, false);
                });

                // Save all recurring instances
                await Promise.all(savePromises);

                // Show success message with count
                alert(`Successfully created ${recurringDates.length} recurring events!`);
            } else {
                // Single event submission (existing logic)
                const formDataToSave = {
                    ...formData,
                    platformImage: platformData,
                    additionalSignatures: additionalSignatures
                };

                // Save the form using ParseAPI helper
                await ParseAPI.saveForm(formDataToSave, user, false);
            }

            // Show success message and navigate
            setFormSaved(true);
            setTimeout(() => {
                navigate('/home/request');
            }, 1500);
        } catch (error) {
            console.error('Error saving form data with Parse:', error);

            // Fallback to the original API if Parse fails
            try {
                const platformData = platformRef.current ? await platformRef.current.exportImage() : null;
                let additionalSignatures = []
                if (formData.campusLockUp === true) { additionalSignatures.push(5) }
                if (formData.securityPersonnel === true || formData.trafficConeAdjustment === true) { additionalSignatures.push(6) }
                if (formData.sprinklerAdjustment === true) { additionalSignatures.push(7) }
                if (formData.platformChangeRequest === true || formData.liveServicesTechRequest === true) { additionalSignatures.push(8) }

                // For fallback API, handle recurring events
                if (formData.isRecurring) {
                    const recurringDates = generateRecurringDates();

                    const savePromises = recurringDates.map(async (eventDate) => {
                        return axios.post(`${SERVER_URL}/save-form`, {
                            owner: user.email,
                            type: 1,
                            formData: {
                                ...formData,
                                dayDateOfEvent: eventDate,
                                recurringSeriesId: `${user.email}_${Date.now()}`,
                                isRecurringInstance: true
                            },
                            platformImage: platformData,
                            additionalSignatures: additionalSignatures
                        });
                    });

                    await Promise.all(savePromises);
                    alert(`Successfully created ${recurringDates.length} recurring events!`);
                } else {
                    await axios.post(`${SERVER_URL}/save-form`, {
                        owner: user.email,
                        type: 1,
                        formData: formData,
                        platformImage: platformData,
                        additionalSignatures: additionalSignatures
                    });
                }

                setFormSaved(true);
                setTimeout(() => {
                    navigate('/home/request');
                }, 1500);
            } catch (fallbackError) {
                console.error('Fallback save also failed:', fallbackError);
                alert('Error saving form data');
            }
        } finally {
            setLoading(false);
        }
    };

    // Handle saving draft with ParseAPI
    const handleSaveDraft = async () => {
        try {
            setLoading(true);
            
            // Save form as draft using ParseAPI helper
            await ParseAPI.saveForm(formData, user, true);
            
            setFormSaved(true);
            setTimeout(() => {
                navigate('/home/request');
            }, 1500);
        } catch (error) {
            console.error('Error saving draft with Parse:', error);
            
            // Fallback to the original API if Parse fails
            try {
                await axios.post(`${SERVER_URL}/save-form`, {
                    owner: user.email, 
                    type: "MasterCalendar", 
                    formData: formData, 
                    isDraft: true
                });
                
                setFormSaved(true);
                setTimeout(() => {
                    navigate('/home/request');
                }, 1500);
            } catch (fallbackError) {
                console.error('Fallback draft save also failed:', fallbackError);
                alert('Error saving form data');
            }
        } finally {
            setLoading(false);
        }
    };


    return(
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {loading && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-5 rounded-lg shadow-xl flex items-center space-x-4">
                        <svg className="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-gray-700 font-medium">Processing...</span>
                    </div>
                </div>
            )}
            
            {formSaved && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-5 rounded-lg shadow-xl flex items-center space-x-4">
                        <svg className="h-8 w-8 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-700 font-medium">Form saved successfully!</span>
                    </div>
                </div>
            )}
            
            <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="space-y-8">
                {/* Header */}
                <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                    <div className="px-6 py-5 border-b border-gray-200 bg-indigo-50">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-2xl font-bold text-indigo-800">On-Campus Event Request Form</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    {isViewOnly 
                                        ? "View mode - form details cannot be edited" 
                                        : "Complete all required fields to submit your event request."}
                                </p>
                                
                                {/* Show notification if this form was converted from a Save the Date */}
                                {formData.convertedFromSaveTheDate && (
                                    <div className="mt-2 text-sm text-yellow-800 bg-yellow-100 p-2 rounded">
                                        This form was created from a Save the Date request. Please fill in all the additional details.
                                    </div>
                                )}
                            </div>
                            <img src="/bethel-favicon.png" alt="Bethel Logo" className="h-12 w-auto" />
                        </div>
                    </div>
                    
                    {isViewOnly && (
                        <div className="bg-blue-50 p-4 border-b border-gray-200">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-blue-700">
                                        You are viewing a submitted form. No changes can be made.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Form Sections */}
                    <div className="border-t border-gray-200">
                        {/* Section 1: Basic Event Information */}
                        <div className="bg-white px-6 py-6">
                            <h4 className="text-xl font-medium text-gray-900 mb-4">Event Information</h4>
                            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                                <div className="sm:col-span-6">
                                    <label htmlFor="eventName" className="block text-sm font-medium text-gray-700">Event Name</label>
                                    <div className="mt-1">
                                        <input 
                                            required 
                                            placeholder="First Day of School" 
                                            type="text" 
                                            name="eventName" 
                                            id="eventName" 
                                            value={formData.eventName} 
                                            onChange={handleChange}
                                            readOnly={isViewOnly}
                                            disabled={isViewOnly}
                                            className={`shadow-sm ${!isViewOnly ? 'focus:ring-indigo-500 focus:border-indigo-500' : ''} block w-full sm:text-sm ${isViewOnly ? 'bg-gray-100' : 'border-gray-300'} rounded-md`}
                                        />
                                    </div>
                                </div>
                                
                                <div className="sm:col-span-3">
                                    <label htmlFor="department" className="block text-sm font-medium text-gray-700">Department</label>
                                    <div className="mt-1">
                                        <input
                                            required
                                            type="text"
                                            name="department"
                                            id="department"
                                            value={formData.department}
                                            onChange={handleChange}
                                            disabled={isViewOnly}
                                            className={`shadow-sm ${!isViewOnly ? 'focus:ring-indigo-500 focus:border-indigo-500' : ''} block w-full sm:text-sm ${isViewOnly ? 'bg-gray-100' : 'border-gray-300'} rounded-md`}
                                            placeholder="Enter department name"
                                        />
                                    </div>
                                </div>
                                
                                <div className="sm:col-span-3">
                                    <label htmlFor="estimatedAttendance" className="block text-sm font-medium text-gray-700">Estimated Attendance</label>
                                    <div className="mt-1">
                                        <input 
                                            required 
                                            type="number" 
                                            name="estimatedAttendance" 
                                            id="estimatedAttendance" 
                                            value={formData.estimatedAttendance} 
                                            onChange={handleChange} 
                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" 
                                            placeholder="0"
                                            min="0"
                                        />
                                    </div>
                                </div>
                                
                                <div className="sm:col-span-6">
                                    <label htmlFor="eventOverseer" className="block text-sm font-medium text-gray-700">Person Overseeing Event</label>
                                    <div className="mt-1">
                                        <input 
                                            required 
                                            type="text" 
                                            name="eventOverseer" 
                                            id="eventOverseer" 
                                            value={formData.eventOverseer} 
                                            onChange={handleChange} 
                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" 
                                            placeholder="John Doe"
                                            disabled={isViewOnly}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Section 2: Date and Time */}
                        <div className="bg-gray-50 px-6 py-6 border-t border-gray-200">
                            <h4 className="text-xl font-medium text-gray-900 mb-4">Date & Time</h4>
                            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                                <div className="sm:col-span-2">
                                    <label htmlFor="dayDateOfEvent" className="block text-sm font-medium text-gray-700">Event Date</label>
                                    <div className="mt-1">
                                        <input 
                                            required 
                                            type="date" 
                                            name="dayDateOfEvent" 
                                            id="dayDateOfEvent" 
                                            value={formData.dayDateOfEvent} 
                                            onChange={handleChange} 
                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" 
                                        />
                                    </div>
                                </div>
                                
                                <div className="sm:col-span-2">
                                    <label htmlFor="startTime" className="block text-sm font-medium text-gray-700">Start Time</label>
                                    <div className="mt-1">
                                        <input 
                                            required 
                                            type="time" 
                                            name="startTime" 
                                            id="startTime" 
                                            value={formData.startTime} 
                                            onChange={handleChange} 
                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" 
                                        />
                                    </div>
                                </div>
                                
                                <div className="sm:col-span-2">
                                    <label htmlFor="endTime" className="block text-sm font-medium text-gray-700">End Time</label>
                                    <div className="mt-1">
                                        <input 
                                            required 
                                            type="time" 
                                            name="endTime" 
                                            id="endTime" 
                                            value={formData.endTime} 
                                            onChange={handleChange} 
                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" 
                                        />
                                    </div>
                                </div>
                                
                                <div className="sm:col-span-3">
                                    <label htmlFor="prepTime" className="block text-sm font-medium text-gray-700">Time Reserved for Prep</label>
                                    <div className="mt-1">
                                        <input 
                                            required 
                                            type="time" 
                                            name="prepTime" 
                                            id="prepTime" 
                                            value={formData.prepTime} 
                                            onChange={handleChange} 
                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" 
                                        />
                                    </div>
                                </div>
                                
                                <div className="sm:col-span-3">
                                    <div className="flex justify-between items-center">
                                        <label htmlFor="locationRequested" className="block text-sm font-medium text-gray-700">Location Requested</label>
                                        <a 
                                            href="/campus-map.pdf" 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                            </svg>
                                            Campus Map
                                        </a>
                                    </div>
                                    <div className="mt-1">
                                        <input 
                                            required 
                                            type="text" 
                                            name="locationRequested" 
                                            id="locationRequested" 
                                            value={formData.locationRequested} 
                                            onChange={handleChange} 
                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                            placeholder="Enter location"
                                            disabled={isViewOnly}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Section 2.5: Recurring Event Settings */}
                        <div className="bg-white px-6 py-6 border-t border-gray-200">
                            <h4 className="text-xl font-medium text-gray-900 mb-4">Recurring Event</h4>
                            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                                <div className="sm:col-span-6">
                                    <div className="flex items-start">
                                        <div className="flex items-center h-5">
                                            <input
                                                type="checkbox"
                                                name="isRecurring"
                                                checked={formData.isRecurring}
                                                onChange={handleChange}
                                                disabled={isViewOnly}
                                                className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-4 w-4 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                                            />
                                        </div>
                                        <div className="ml-3">
                                            <label htmlFor="isRecurring" className="font-medium text-gray-700">
                                                This is a recurring event
                                            </label>
                                            <p className="text-sm text-gray-500">
                                                Enable this to create multiple events with the same details on different dates
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {formData.isRecurring && (
                                    <>
                                        <div className="sm:col-span-2">
                                            <label htmlFor="recurrenceType" className="block text-sm font-medium text-gray-700">
                                                Repeat Pattern
                                            </label>
                                            <div className="mt-1">
                                                <select
                                                    name="recurrenceType"
                                                    id="recurrenceType"
                                                    value={formData.recurrenceType}
                                                    onChange={handleChange}
                                                    disabled={isViewOnly}
                                                    className={`shadow-sm ${!isViewOnly ? 'focus:ring-indigo-500 focus:border-indigo-500' : ''} block w-full sm:text-sm ${isViewOnly ? 'bg-gray-100' : 'border-gray-300'} rounded-md`}
                                                >
                                                    <option value="daily">Daily</option>
                                                    <option value="weekly">Weekly</option>
                                                    <option value="monthly">Monthly</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="sm:col-span-2">
                                            <label htmlFor="recurrenceInterval" className="block text-sm font-medium text-gray-700">
                                                Repeat Every
                                            </label>
                                            <div className="mt-1 flex items-center">
                                                <input
                                                    type="number"
                                                    name="recurrenceInterval"
                                                    id="recurrenceInterval"
                                                    value={formData.recurrenceInterval}
                                                    onChange={handleChange}
                                                    min="1"
                                                    max="30"
                                                    disabled={isViewOnly}
                                                    className={`shadow-sm ${!isViewOnly ? 'focus:ring-indigo-500 focus:border-indigo-500' : ''} block w-20 sm:text-sm ${isViewOnly ? 'bg-gray-100' : 'border-gray-300'} rounded-md`}
                                                />
                                                <span className="ml-3 text-sm text-gray-700">
                                                    {formData.recurrenceType === 'daily' && 'day(s)'}
                                                    {formData.recurrenceType === 'weekly' && 'week(s)'}
                                                    {formData.recurrenceType === 'monthly' && 'month(s)'}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="sm:col-span-2">
                                            <label htmlFor="recurrenceEndDate" className="block text-sm font-medium text-gray-700">
                                                End Date
                                            </label>
                                            <div className="mt-1">
                                                <input
                                                    type="date"
                                                    name="recurrenceEndDate"
                                                    id="recurrenceEndDate"
                                                    value={formData.recurrenceEndDate}
                                                    onChange={handleChange}
                                                    min={formData.dayDateOfEvent}
                                                    required={formData.isRecurring}
                                                    disabled={isViewOnly}
                                                    className={`shadow-sm ${!isViewOnly ? 'focus:ring-indigo-500 focus:border-indigo-500' : ''} block w-full sm:text-sm ${isViewOnly ? 'bg-gray-100' : 'border-gray-300'} rounded-md`}
                                                />
                                            </div>
                                        </div>

                                        {formData.recurrenceType === 'weekly' && (
                                            <div className="sm:col-span-6">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Repeat on Days
                                                </label>
                                                <div className="flex flex-wrap gap-3">
                                                    {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => (
                                                        <label key={day} className="flex items-center">
                                                            <input
                                                                type="checkbox"
                                                                value={index}
                                                                checked={formData.recurrenceDays.includes(index)}
                                                                onChange={(e) => {
                                                                    const dayIndex = parseInt(e.target.value);
                                                                    const newDays = e.target.checked
                                                                        ? [...formData.recurrenceDays, dayIndex]
                                                                        : formData.recurrenceDays.filter(d => d !== dayIndex);
                                                                    setFormData({
                                                                        ...formData,
                                                                        recurrenceDays: newDays.sort()
                                                                    });
                                                                }}
                                                                disabled={isViewOnly}
                                                                className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-4 w-4 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                                                            />
                                                            <span className="ml-2 text-sm text-gray-700">{day}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {formData.isRecurring && formData.dayDateOfEvent && formData.recurrenceEndDate && (
                                            <div className="sm:col-span-6">
                                                <div className="bg-blue-50 p-4 rounded-md">
                                                    <div className="flex">
                                                        <div className="flex-shrink-0">
                                                            <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                                            </svg>
                                                        </div>
                                                        <div className="ml-3">
                                                            <p className="text-sm text-blue-700">
                                                                This will create approximately {calculateRecurrenceCount()} events based on your recurrence settings.
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Section 3: SLOs Alignment */}
                        <div className="bg-white px-6 py-6 border-t border-gray-200">
                            <h4 className="text-xl font-medium text-gray-900 mb-4">SLOs Alignment</h4>
                            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                                <div className="sm:col-span-3">
                                    <fieldset>
                                        <legend className="text-sm font-medium text-gray-700 mb-2">Please select all that apply:</legend>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2">
                                            <div className="flex items-start">
                                                <div className="flex items-center h-5">
                                                    <input 
                                                        type="checkbox" 
                                                        name="criticalThinkers" 
                                                        checked={formData.criticalThinkers} 
                                                        onChange={handleChange}
                                                        disabled={isViewOnly}
                                                        className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-4 w-4 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                                                    />
                                                </div>
                                                <div className="ml-3 text-sm">
                                                    <label htmlFor="criticalThinkers" className="font-medium text-gray-700">Critical & Creative Thinkers</label>
                                                </div>
                                            </div>
                                            <div className="flex items-start">
                                                <div className="flex items-center h-5">
                                                    <input
                                                        type="checkbox"
                                                        name="competentCommunicators"
                                                        checked={formData.competentCommunicators}
                                                        onChange={handleChange}
                                                        disabled={isViewOnly}
                                                        className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-4 w-4 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                                                    />
                                                </div>
                                                <div className="ml-3 text-sm">
                                                    <label htmlFor="competentCommunicators" className="font-medium text-gray-700">Competent Communicators</label>
                                                </div>
                                            </div>
                                            <div className="flex items-start">
                                                <div className="flex items-center h-5">
                                                    <input
                                                        type="checkbox"
                                                        name="communityContributors"
                                                        checked={formData.communityContributors}
                                                        onChange={handleChange}
                                                        disabled={isViewOnly}
                                                        className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-4 w-4 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                                                    />
                                                </div>
                                                <div className="ml-3 text-sm">
                                                    <label htmlFor="communityContributors" className="font-medium text-gray-700">Community Contributors</label>
                                                </div>
                                            </div>
                                            <div className="flex items-start">
                                                <div className="flex items-center h-5">
                                                    <input
                                                        type="checkbox"
                                                        name="lifeLongLearners"
                                                        checked={formData.lifeLongLearners}
                                                        onChange={handleChange}
                                                        disabled={isViewOnly}
                                                        className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-4 w-4 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                                                    />
                                                </div>
                                                <div className="ml-3 text-sm">
                                                    <label htmlFor="lifeLongLearners" className="font-medium text-gray-700">Life-long Learners</label>
                                                </div>
                                            </div>
                                            <div className="flex items-start">
                                                <div className="flex items-center h-5">
                                                    <input
                                                        type="checkbox"
                                                        name="christCentered"
                                                        checked={formData.christCentered}
                                                        onChange={handleChange}
                                                        disabled={isViewOnly}
                                                        className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-4 w-4 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                                                    />
                                                </div>
                                                <div className="ml-3 text-sm">
                                                    <label htmlFor="christCentered" className="font-medium text-gray-700">Christ-Centered</label>
                                                </div>
                                            </div>
                                        </div>
                                    </fieldset>
                                </div>
                                
                                <div className="sm:col-span-3">
                                    <label htmlFor="sloExplanation" className="block text-sm font-medium text-gray-700">Explanation</label>
                                    <div className="mt-1">
                                        <textarea
                                            name="sloExplanation"
                                            id="sloExplanation"
                                            rows="4"
                                            value={formData.sloExplanation}
                                            onChange={handleChange}
                                            disabled={isViewOnly}
                                            readOnly={isViewOnly}
                                            className={`shadow-sm ${!isViewOnly ? 'focus:ring-indigo-500 focus:border-indigo-500' : ''} block w-full sm:text-sm ${isViewOnly ? 'bg-gray-100' : 'border-gray-300'} rounded-md`}
                                            placeholder="Explain how your event aligns with the SLOs"
                                        ></textarea>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Section 4: Additional Services */}
                        <div className="bg-gray-50 px-6 py-6 border-t border-gray-200">
                            <h4 className="text-xl font-medium text-gray-900 mb-4">Additional Services</h4>
                            <p className="text-sm text-gray-500 mb-4">Select all services that your event will require.</p>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-4 gap-x-6">
                                <div className="relative flex items-start">
                                    <div className="flex items-center h-5">
                                        <input 
                                            type="checkbox" 
                                            name="campusLockUp" 
                                            checked={formData.campusLockUp} 
                                            onChange={handleChange}
                                            disabled={isViewOnly}
                                            className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-5 w-5 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                                        />
                                    </div>
                                    <div className="ml-3 text-sm">
                                        <label htmlFor="campusLockUp" className="font-medium text-gray-700">Campus Lock-Up</label>
                                    </div>
                                </div>
                                
                                <div className="relative flex items-start">
                                    <div className="flex items-center h-5">
                                        <input
                                            type="checkbox"
                                            name="equipmentRequest"
                                            checked={formData.equipmentRequest}
                                            onChange={handleChange}
                                            disabled={isViewOnly}
                                            className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-5 w-5 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                                        />
                                    </div>
                                    <div className="ml-3 text-sm">
                                        <label htmlFor="equipmentRequest" className="font-medium text-gray-700">Equipment Request</label>
                                    </div>
                                </div>

                                <div className="relative flex items-start">
                                    <div className="flex items-center h-5">
                                        <input
                                            type="checkbox"
                                            name="platformChangeRequest"
                                            checked={formData.platformChangeRequest}
                                            onChange={handleChange}
                                            disabled={isViewOnly}
                                            className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-5 w-5 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                                        />
                                    </div>
                                    <div className="ml-3 text-sm">
                                        <label htmlFor="platformChangeRequest" className="font-medium text-gray-700">Platform Change Request</label>
                                    </div>
                                </div>

                                <div className="relative flex items-start">
                                    <div className="flex items-center h-5">
                                        <input
                                            type="checkbox"
                                            name="liveServicesTechRequest"
                                            checked={formData.liveServicesTechRequest}
                                            onChange={handleChange}
                                            disabled={isViewOnly}
                                            className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-5 w-5 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                                        />
                                    </div>
                                    <div className="ml-3 text-sm">
                                        <label htmlFor="liveServicesTechRequest" className="font-medium text-gray-700">Live Services/Performing Arts Tech</label>
                                    </div>
                                </div>

                                <div className="relative flex items-start">
                                    <div className="flex items-center h-5">
                                        <input
                                            type="checkbox"
                                            name="securityPersonnel"
                                            checked={formData.securityPersonnel}
                                            onChange={handleChange}
                                            disabled={isViewOnly}
                                            className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-5 w-5 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                                        />
                                    </div>
                                    <div className="ml-3 text-sm">
                                        <label htmlFor="securityPersonnel" className="font-medium text-gray-700">Security Personnel</label>
                                    </div>
                                </div>

                                <div className="relative flex items-start">
                                    <div className="flex items-center h-5">
                                        <input
                                            type="checkbox"
                                            name="sprinklerAdjustment"
                                            checked={formData.sprinklerAdjustment}
                                            onChange={handleChange}
                                            disabled={isViewOnly}
                                            className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-5 w-5 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                                        />
                                    </div>
                                    <div className="ml-3 text-sm">
                                        <label htmlFor="sprinklerAdjustment" className="font-medium text-gray-700">Sprinkler Adjustment</label>
                                    </div>
                                </div>
                                
                                <div className="relative flex items-start">
                                    <div className="flex items-center h-5">
                                        <input
                                            type="checkbox"
                                            name="trafficConeAdjustment"
                                            checked={formData.trafficConeAdjustment}
                                            onChange={handleChange}
                                            disabled={isViewOnly}
                                            className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-5 w-5 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                                        />
                                    </div>
                                    <div className="ml-3 text-sm">
                                        <label htmlFor="trafficConeAdjustment" className="font-medium text-gray-700">Traffic Cone Adjustment</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                                                
                        {/* Conditional Forms with improved styling */}
                        {formData.campusLockUp && (
                            <div className="border-t border-gray-200 bg-white p-6">
                                <h4 className="text-lg font-medium text-indigo-800 mb-4">Campus Lock-Up Details</h4>
                                <CampusLockUp
                                    onUpdate={(data) => handleUpdate('campusLockUpForm', data)}
                                    active={formData.campusLockUp}
                                    isViewOnly={isViewOnly}
                                    initialData={formData.campusLockUpForm}
                                />
                            </div>
                        )}
                        
                        {formData.equipmentRequest && (
                            <div className="border-t border-gray-200 bg-white p-6">
                                <h4 className="text-lg font-medium text-indigo-800 mb-4">Equipment Request Details</h4>
                                <EquipmentRequest
                                    onUpdate={(data) => handleUpdate('equipmentRequestForm', data)}
                                    active={formData.equipmentRequest}
                                    isViewOnly={isViewOnly}
                                    initialData={formData.equipmentRequestForm}
                                />
                            </div>
                        )}
                        
                        {formData.platformChangeRequest && (
                            <div className="border-t border-gray-200 bg-white p-6">
                                <h4 className="text-lg font-medium text-indigo-800 mb-4">Platform Change Request Details</h4>
                                <PlatformChangeRequestForm
                                    ref={platformRef}
                                    onUpdate={(data) => handleUpdate('platformChangeRequestForm', data)}
                                    active={formData.platformChangeRequest}
                                    isViewOnly={isViewOnly}
                                    initialData={formData.platformChangeRequestForm}
                                />
                            </div>
                        )}

                        {formData.liveServicesTechRequest && (
                            <div className="border-t border-gray-200 bg-white p-6">
                                <h4 className="text-lg font-medium text-indigo-800 mb-4">Live Services Tech Request Details</h4>
                                <LiveServicesTechRequestForm
                                    onUpdate={(data) => handleUpdate('liveServicesTechRequestForm', data)}
                                    active={formData.liveServicesTechRequest}
                                    isViewOnly={isViewOnly}
                                    initialData={formData.liveServicesTechRequestForm}
                                />
                            </div>
                        )}

                        {formData.liveServicesTechRequest && (
                            <div className="border-t border-gray-200 bg-white p-6">
                                <h4 className="text-lg font-medium text-indigo-800 mb-4">Performing Arts Tech Request Details</h4>
                                <PerformingArtsTechRequestForm
                                    onUpdate={(data) => handleUpdate('performingArtsTechRequestForm', data)}
                                    active={formData.liveServicesTechRequest}
                                    isViewOnly={isViewOnly}
                                    initialData={formData.performingArtsTechRequestForm}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Form Actions */}
                {!isViewOnly ? (
                    <div className="px-6 py-4 bg-gray-50 rounded-lg shadow flex justify-end space-x-4">
                        <button 
                            type="button" 
                            onClick={handleSaveDraft} 
                            disabled={loading}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            Save as Draft
                        </button>
                        <button 
                            type="submit" 
                            disabled={loading}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            Submit Request
                        </button>
                    </div>
                ) : (
                    <div className="px-6 py-4 bg-gray-50 rounded-lg shadow flex justify-end space-x-4">
                        <button 
                            type="button" 
                            onClick={() => navigate('/home/request')}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            Back to My Requests
                        </button>
                    </div>
                )}
            </form>
        </div>
    )

}